
import React from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Ruler, Weight, Info } from "lucide-react";
import { ParcelValidationErrors } from "@/hooks/useParcelValidation";
import ErrorMessage from "./ErrorMessage";

interface ParcelDimensionsFieldsProps {
  dimensions: {
    length: string;
    width: string;
    height: string;
  };
  weight: string;
  onDimensionChange: (field: string, value: string) => void;
  onWeightChange: (value: string) => void;
  errors?: ParcelValidationErrors;
}

const ParcelDimensionsFields: React.FC<ParcelDimensionsFieldsProps> = ({
  dimensions,
  weight,
  onDimensionChange,
  onWeightChange,
  errors = {}
}) => {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Length */}
        <div className="space-y-2">
          <Label className={errors.length ? "validation-error" : ""}>Length (cm) *</Label>
          <div className="flex items-center">
            <Input
              type="number"
              value={dimensions.length}
              onChange={(e) => onDimensionChange('length', e.target.value)}
              placeholder="Length"
              className={errors.length ? "validation-error-border" : ""}
            />
            <Ruler className="ml-1 h-4 w-4 text-muted-foreground" />
          </div>
          <ErrorMessage error={errors.length} />
        </div>

        {/* Width */}
        <div className="space-y-2">
          <Label className={errors.width ? "validation-error" : ""}>Width (cm) *</Label>
          <div className="flex items-center">
            <Input
              type="number"
              value={dimensions.width}
              onChange={(e) => onDimensionChange('width', e.target.value)}
              placeholder="Width"
              className={errors.width ? "validation-error-border" : ""}
            />
            <Ruler className="ml-1 h-4 w-4 text-muted-foreground" />
          </div>
          <ErrorMessage error={errors.width} />
        </div>

        {/* Height */}
        <div className="space-y-2">
          <Label className={errors.height ? "validation-error" : ""}>Height (cm) *</Label>
          <div className="flex items-center">
            <Input
              type="number"
              value={dimensions.height}
              onChange={(e) => onDimensionChange('height', e.target.value)}
              placeholder="Height"
              className={errors.height ? "validation-error-border" : ""}
            />
            <Ruler className="ml-1 h-4 w-4 text-muted-foreground" />
          </div>
          <ErrorMessage error={errors.height} />
        </div>

        {/* Weight */}
        <div className="space-y-2">
          <Label className={errors.weight ? "validation-error" : ""}>Weight (kg) *</Label>
          <div className="flex items-center">
            <Input
              type="number"
              value={weight}
              onChange={(e) => onWeightChange(e.target.value)}
              placeholder="Enter weight in kilograms"
              className={errors.weight ? "validation-error-border" : ""}
            />
            <Weight className="ml-2 h-5 w-5 text-muted-foreground" />
          </div>
          <ErrorMessage error={errors.weight} />
        </div>
      </div>

      {/* Informational Note */}
      <div className="flex items-start gap-2 text-xs text-muted-foreground">
        <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        <p>Quoted prices are based on the provided weight and dimensions. If actual details differ, additional charges may apply.</p>
      </div>
    </div>
  );
};

export default ParcelDimensionsFields;
